import React, { useMemo } from 'react';
import { Col, Row, Typography } from 'antd';
import styled from 'styled-components';
import { SearchUrlType, Colors } from '@copilot/common/utils/constant';
import { InfoCircleOutlined } from '@ant-design/icons';
import { UtilityFunctions } from '@copilot/common/utils/common';

const { Text, Link, Paragraph } = Typography;

interface SearchUrlStatusModuleProps {
	searchType: SearchUrlType;
	isInitialSearch: boolean;
	numPeople: number;
	updateDate?: string;
	url?: string;
}
const disclaimerText =
	'To prevent you from LinkedIn restrictions, prospects that you have previously sent invites were not collected.';

const SearchUrlStatusModule: React.FC<SearchUrlStatusModuleProps> = (props) => {
	const { searchType, isInitialSearch, numPeople, updateDate, url } = props;

	const initialSearch = useMemo(() => {
		let text = '';
		switch (searchType) {
			case SearchUrlType.Search:
				text = "There's no current prospects in your list.";
				break;
			case SearchUrlType.Nurture:
				text =
					'Import 1st degree connections directly from LinkedIn into this Nurture List';
				break;
			default:
				UtilityFunctions.assertUnreachable(searchType);
		}
		return (
			<Col>
				<Paragraph>{`${text}`}</Paragraph>
			</Col>
		);
	}, [searchType]);

	const urlInfoText = useMemo(() => {
		switch (searchType) {
			case SearchUrlType.Search:
				return 'Current search URL';
			case SearchUrlType.Nurture:
				return 'Below is the last Sales Navigator URL you used to add connections to this Nurture List.';
			default:
				return UtilityFunctions.assertUnreachable(searchType);
		}
	}, [searchType]);

	const numPeopleText = useMemo(() => {
		switch (searchType) {
			case SearchUrlType.Search:
				return (
					<Col>
						{"We've collected "}
						<Text strong>{`${numPeople} prospects \n `}</Text>
						from this URL.
						<Paragraph>
							<InfoCircleOutlined />
							{'  '}
							{disclaimerText}
						</Paragraph>
					</Col>
				);
			case SearchUrlType.Nurture:
				return null;
			default:
				return UtilityFunctions.assertUnreachable(searchType);
		}
	}, [searchType, numPeople]);

	const updateDateLocaleString = useMemo(
		() => updateDate && ` (Last updated ${new Date(updateDate).toLocaleDateString()}):`,
		[updateDate]
	);
	return (
		<Row
			style={{
				width: '90%',
				height: '90%',
				backgroundColor: Colors.WizardBackground,
				borderRadius: '5px',
			}}
		>
			{isInitialSearch ? (
				initialSearch
			) : (
				<Col style={{ width: '80%', height: '80%', padding: '30px' }}>
					{urlInfoText}
					{updateDateLocaleString}
					{url && (
						<Ellipsis>
							<Link underline target="_blank" href={url}>
								{url}
							</Link>
						</Ellipsis>
					)}
					{numPeopleText}
				</Col>
			)}
		</Row>
	);
};

const Ellipsis = styled.p`
	text-overflow: ellipsis;
	white-space: nowrap;
	overflow: hidden;
`;

export default SearchUrlStatusModule;
